import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Heart, ArrowLeft, X } from "lucide-react";
import { UAE_LOCATIONS } from "@/constants/categories";

interface StepLocationProps {
  onNext: (location: string, interests: string[]) => void;
  onBack: () => void;
}

const INTEREST_OPTIONS = [
  'Health & Fitness',
  'Education',
  'Volunteering',
  'Career',
  'Family',
  'Finance',
  'Environment',
  'Technology',
  'Arts & Culture',
  'Social Impact',
];

const EMIRATE_ICONS: Record<string, string> = {
  'UAE': '🇦🇪',
  'Abu Dhabi': '🏛️',
  'Dubai': '🏙️',
  'Sharjah': '📚',
  'Ajman': '🌊',
  'Umm Al Quwain': '🐚',
  'Ras Al Khaimah': '⛰️',
  'Fujairah': '🏖️',
};

export function StepLocation({ onNext, onBack }: StepLocationProps) {
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);

  const toggleInterest = (interest: string) => {
    setSelectedInterests(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  return (
    <div className="space-y-8 text-center">
      <div className="space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#F97316] to-[#D946EF] mb-2">
          <MapPin className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold">Help Us Personalize 🎯</h2>
        <p className="text-muted-foreground text-lg">
          We'll show you offers and content relevant to your area and interests
        </p>
      </div>

      {/* Emirate Selection */}
      <div className="space-y-3">
        <label className="text-lg font-semibold flex items-center justify-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          Where are you based?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {UAE_LOCATIONS.map((loc) => (
            <button
              type="button"
              key={loc}
              onClick={() => setSelectedLocation(loc)}
              className={`p-3 rounded-xl border-2 transition-all hover:scale-[1.02] text-left flex items-center gap-2 ${
                selectedLocation === loc
                  ? "border-primary bg-primary/10 shadow-md"
                  : "border-border hover:border-muted-foreground"
              }`}
            >
              <span className="text-xl">{EMIRATE_ICONS[loc] || '📍'}</span>
              <span className="font-medium text-sm">{loc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Interests Selection */}
      <div className="space-y-3">
        <label className="text-lg font-semibold flex items-center justify-center gap-2">
          <Heart className="h-5 w-5 text-primary" />
          What are you interested in?
        </label>
        <div className="flex flex-wrap gap-2 justify-center">
          {INTEREST_OPTIONS.map(interest => {
            const selected = selectedInterests.includes(interest);
            return (
              <Badge
                key={interest}
                variant={selected ? "default" : "outline"}
                className={`cursor-pointer select-none px-3 py-1.5 text-sm transition-all hover:scale-105 ${
                  selected ? "shadow-md" : ""
                }`}
                onClick={() => toggleInterest(interest)}
              >
                {interest}
                {selected && <X className="h-3 w-3 ml-1" />}
              </Badge>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          Select as many as you like — this helps us suggest relevant goals and offers
        </p>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="flex-1 text-lg"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <Button
          type="button"
          size="lg"
          className="flex-1 text-lg bg-gradient-to-r from-[#F97316] to-[#D946EF] hover:opacity-90"
          onClick={() => onNext(selectedLocation, selectedInterests)}
        >
          {selectedLocation || selectedInterests.length > 0 ? "Next Step →" : "Skip for Now →"}
        </Button>
      </div>
    </div>
  );
}
